import { isEmpty, partition } from 'lodash';
import { DEFAULT_SUPPORTED_CURRENCIES } from '../../../constants/analytics';
import { DATE_TYPE_DAYS, DATE_TYPE_TOTAL } from '../../tables/reports/consts';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';

export function getMetricsFromData(data, currency) {
  const totalOrdersCount = data
    .filter((item) => 'total_orders_count' in item)
    .reduce((acc, curr) => acc + curr.total_orders_count, 0);

  const totalReturnsCount = data
    .filter((item) => 'total_returns_count' in item)
    .reduce((acc, curr) => acc + curr.total_returns_count, 0);

  const totalCancellationsCount = data
    .filter((item) => 'total_cancellations_count' in item)
    .reduce((acc, curr) => acc + curr.total_cancellations_count, 0);

  const dataWithGmv = data.filter((item) => 'gmv' in item);

  const totalGmv = dataWithGmv.map((item) => item.gmv[currency].all_orders).reduce((acc, curr) => acc + curr, 0);
  const totalCancelledOrdersGmv = dataWithGmv
    .map((item) => getTotalCancellationValue(item.gmv[currency]))
    .reduce((acc, curr) => acc + curr, 0);
  const totalReturnsGmv = dataWithGmv.map((item) => item.gmv[currency].returns).reduce((acc, curr) => acc + curr, 0);
  const totalNetSales = totalGmv - totalCancelledOrdersGmv - totalReturnsGmv;

  const dataWithUnitCount = data.filter((item) => 'unit_count' in item);
  const totalOrdersUnits = dataWithUnitCount
    .map((item) => item.unit_count.all_orders)
    .reduce((acc, curr) => acc + curr, 0);
  const totalCancelledUnits = dataWithUnitCount
    .map((item) => getTotalCancellationValue(item.unit_count))
    .reduce((acc, curr) => acc + curr, 0);
  const totalReturnedUnits = dataWithUnitCount
    .map((item) => item.unit_count.returns)
    .reduce((acc, curr) => acc + curr, 0);

  return {
    totalOrdersCount: totalOrdersCount,
    totalReturnsCount: totalReturnsCount,
    totalCancellationsCount: totalCancellationsCount,
    gmv: {
      allOrders: totalGmv,
      cancelledOrders: totalCancelledOrdersGmv,
      returns: totalReturnsGmv,
      netSales: totalNetSales,
    },
    unitCount: {
      allOrders: totalOrdersUnits,
      cancelledOrders: totalCancelledUnits,
      returns: totalReturnedUnits,
    },
  };
}

export function setDateColumnByDateType(columns, dateColumn, dateType) {
  if (dateType === DATE_TYPE_TOTAL) {
    return columns.filter((column) => column !== 'date');
  }

  if (dateType === DATE_TYPE_DAYS) {
    dateColumn.label = 'Date';
  } else {
    dateColumn.label = 'Week End Date';
  }
  return columns;
}

export function calculatePercentageChange(value, prevValue) {
  if (isNaN(Number(prevValue)) || Number(prevValue) === 0) {
    return null;
  }
  const diff = Number(value) - Number(prevValue);
  if (!diff) return null;

  return (diff / Number(prevValue)) * 100;
}

export function getTotalCancellationValue(item) {
  const rejected =
    item.rejected_orders === item.partial_rejections
      ? item.rejected_orders
      : item.rejected_orders + item.partial_rejections;
  return item.cancelled_orders + rejected;
}

export function doesSupplierSupportMultipleCurrencies(supplierSettings) {
  if (isEmpty(supplierSettings?.analytics)) return true;
  const { supported_currencies = [] } = supplierSettings.analytics;

  return supported_currencies.length > 1;
}

export function getSupportedCurrenciesForSupplier(supplierSettings) {
  if (!supplierSettings?.analytics?.enabled_for_visibility) {
    return DEFAULT_SUPPORTED_CURRENCIES;
  }

  return isEmpty(supplierSettings.analytics.supported_currencies)
    ? DEFAULT_SUPPORTED_CURRENCIES
    : supplierSettings.analytics.supported_currencies.map((currency) => currency.toLowerCase());
}

export function getSalesChannelsOptions(supplierRetailers = [], retailers = [], enabledRetailers = []) {
  const channelsOptions = [];
  const retailersWithStatuses = supplierRetailerUtils.setRetailersActiveStatus(retailers);
  const sortRetailersCallback = sortByDisplayName(supplierRetailers);
  const [activeRetailers, inactiveRetailers] = partition(retailersWithStatuses, ({ active }) => active).map(
    (retailers) => retailers.sort(sortRetailersCallback),
  );

  if (activeRetailers.length) {
    channelsOptions.push({ separator: true, separatorLabel: 'ACTIVE' });
    channelsOptions.push(...activeRetailers);
  }

  if (inactiveRetailers.length) {
    channelsOptions.push({ separator: true, separatorLabel: 'INACTIVE' });
    channelsOptions.push(...inactiveRetailers);
  }

  const mapSalesChannelToOptionsCallback = mapSalesChannelToListOption(supplierRetailers, enabledRetailers);
  return channelsOptions.map(mapSalesChannelToOptionsCallback);
}

function sortByDisplayName(supplierRetailers) {
  return (retailerA, retailerB) => {
    const retailerNameA = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
      supplierRetailers,
      retailer: retailerA,
      retailerId: retailerA.id,
    });
    const retailerNameB = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
      supplierRetailers,
      retailer: retailerB,
      retailerId: retailerB.id,
    });
    return retailerNameA.localeCompare(retailerNameB);
  };
}

function mapSalesChannelToListOption(supplierRetailers, enabledRetailers = []) {
  return (salesChannel) => {
    if (salesChannel.separator) return salesChannel;

    const label = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
      supplierRetailers,
      retailer: salesChannel,
      retailerId: salesChannel.id,
    });
    const isEnabled = enabledRetailers.length && enabledRetailers.map(({ id }) => id).includes(salesChannel.id);
    return { value: salesChannel.id, label, disabled: !isEnabled };
  };
}

export function getRelevantRetailersForOrderTypeFilter(retailers, selectedOrderTypes, retailerIdByOrderType) {
  const isSingleOrderTypeSelected = selectedOrderTypes?.length === 1;
  if (!isSingleOrderTypeSelected) return retailers;
  const retailersWithSelectedType = retailerIdByOrderType[selectedOrderTypes[0]];
  return retailers.filter((retailer) => retailersWithSelectedType.includes(retailer.id));
}
